import React, { useState, useEffect } from 'react';

interface CryptoIconProps {
  symbol: string;
  size?: number;
  className?: string;
}

// Interface for currency data from the API
interface Currency {
  ticker: string;
  name: string;
  image: string;
  hasExternalId: boolean;
  isExtraIdSupported: boolean;
  isFiat: boolean;
  featured: boolean;
  isStable: boolean;
  supportsFixedRate: boolean;
}

// Cache for currency data to avoid repeated API calls
const currencyCache: Record<string, string> = {};

const CryptoIcon: React.FC<CryptoIconProps> = ({ symbol, size = 24, className = '' }) => {
  const [iconUrl, setIconUrl] = useState<string | null>(null);
  const normalizedSymbol = symbol.toLowerCase();

  useEffect(() => {
    const fetchCurrencyData = async () => {
      // If we already have this symbol in cache, use it
      if (currencyCache[normalizedSymbol]) {
        setIconUrl(currencyCache[normalizedSymbol]);
        return;
      }

      try {
        // Fetch currency list from ChangeNOW API if not already loaded
        if (Object.keys(currencyCache).length === 0) {
          const response = await fetch('https://api.changenow.io/v1/currencies?active=true');
          if (response.ok) {
            const currencies: Currency[] = await response.json();
            
            // Populate cache with all currencies
            currencies.forEach(currency => {
              currencyCache[currency.ticker.toLowerCase()] = currency.image;
            });
            
            // Check if our symbol is in the results
            const currency = currencies.find(c => 
              c.ticker.toLowerCase() === normalizedSymbol
            );
            
            if (currency) {
              setIconUrl(currency.image);
            } else {
              setIconUrl(null);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching currency data:', error);
        setIconUrl(null);
      }
    };

    fetchCurrencyData();
  }, [normalizedSymbol]);

  if (iconUrl) {
    return (
      <div className={`crypto-icon ${className}`} style={{ width: size, height: size }}>
        <img 
          src={iconUrl}
          alt={`${symbol} icon`}
          width={size}
          height={size}
        />
      </div>
    );
  }

  // Fallback to simple SVG with text
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      width={size} 
      height={size} 
      className={className}
    >
      <circle cx="12" cy="12" r="12" fill="#f0f0f0"/>
      <text x="12" y="16" fontSize="10" textAnchor="middle" fill="#333" fontWeight="bold">
        {symbol.substring(0, 3).toUpperCase()}
      </text>
    </svg>
  );
};

export default CryptoIcon;


